'use client';

import { useActionState, useId, useState } from 'react';
import Link from 'next/link';
import { sectionIds, site } from '@/content/site';
import { submitContact } from '@/features/contact/actions';
import type { ContactErrorCode, ContactField, ContactState } from '@/features/contact/schema';
import type { Locale } from '@/i18n/config';
import type { Dictionary } from '@/i18n/dictionary';
import { ArrowRight, Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { LogoMark } from '@/components/ui/logo';
import { Reveal } from '@/components/ui/reveal';
import { cn } from '@/lib/cn';

type ContactProps = {
  dictionary: Dictionary;
  locale: Locale;
};

const controlClass =
  'w-full rounded-xl border bg-canvas px-4 py-3 text-base transition-colors focus-visible:border-accent';

export function Contact({ dictionary, locale }: ContactProps) {
  const [attempt, setAttempt] = useState(0);

  return (
    <section id={sectionIds.contact} className="py-section">
      <div className="container-page grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] gap-[clamp(40px,6vw,96px)]">
        <Reveal className="flex flex-col gap-7">
          <Eyebrow>07 / 07 · {dictionary.nav.contact}</Eyebrow>
          <h2 className="max-w-[11ch] text-[clamp(42px,6vw,88px)] leading-[0.96] font-light tracking-[-0.045em]">
            {dictionary.contact.title}
          </h2>
          <p className="max-w-[38ch] text-[clamp(18px,1.4vw,22px)] leading-snug text-muted">
            {dictionary.contact.subtitle}
          </p>
          <div className="mt-2 flex max-w-[38ch] flex-col gap-1.5 border-t border-line pt-6 text-[15px] text-muted">
            <a href={`mailto:${site.email}`} className="transition-colors hover:text-accent">
              {site.email}
            </a>
            <a href={`tel:${site.phoneHref}`} className="transition-colors hover:text-accent">
              {site.phone}
            </a>
            <span className="mt-2.5 text-[13px]">{dictionary.contact.note}</span>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <ContactForm
            key={attempt}
            dictionary={dictionary}
            locale={locale}
            onReset={() => setAttempt((value) => value + 1)}
          />
        </Reveal>
      </div>
    </section>
  );
}

function ContactForm({ dictionary, locale, onReset }: ContactProps & { onReset: () => void }) {
  const [state, formAction, pending] = useActionState<ContactState, FormData>(submitContact, {
    status: 'idle',
  });
  const [topic, setTopic] = useState('');
  const [dismissed, setDismissed] = useState<ContactField[]>([]);
  const fieldId = useId();
  const { contact } = dictionary;

  if (state.status === 'sent') {
    return <SuccessPanel dictionary={dictionary} onReset={onReset} />;
  }

  const errorFor = (field: ContactField): ContactErrorCode | undefined => {
    if (state.status !== 'invalid' || dismissed.includes(field)) return undefined;
    return state.errors[field];
  };

  const dismiss = (field: ContactField) =>
    setDismissed((fields) => (fields.includes(field) ? fields : [...fields, field]));

  const fieldProps = (field: ContactField, extraClass = 'min-h-13.5') => {
    const code = errorFor(field);
    return {
      id: `${fieldId}-${field}`,
      name: field,
      'aria-invalid': code ? true : undefined,
      'aria-describedby': code ? `${fieldId}-${field}-error` : undefined,
      onChange: () => dismiss(field),
      className: cn(controlClass, code ? 'border-danger' : 'border-line', extraClass),
    };
  };

  const label = (field: ContactField, text: string, required = false) => (
    <label htmlFor={`${fieldId}-${field}`} className="text-[13px] font-medium tracking-[0.02em]">
      {text}
      {required && (
        <span aria-hidden className="text-accent">
          {' '}
          *
        </span>
      )}
    </label>
  );

  const error = (field: ContactField) => {
    const code = errorFor(field);
    if (!code) return null;
    return (
      <p
        id={`${fieldId}-${field}-error`}
        role="alert"
        className="text-[13px] font-medium text-danger"
      >
        {contact.errors[code]}
      </p>
    );
  };

  return (
    <form
      action={formAction}
      noValidate
      className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] gap-x-4 gap-y-5.5 rounded-3xl border border-line bg-surface p-[clamp(22px,3vw,40px)]"
    >
      <input type="hidden" name="locale" value={locale} />

      <div className="flex flex-col gap-2">
        {label('name', contact.labels.name, true)}
        <input type="text" autoComplete="name" {...fieldProps('name')} />
        {error('name')}
      </div>

      <div className="flex flex-col gap-2">
        {label('company', contact.labels.company)}
        <input type="text" autoComplete="organization" {...fieldProps('company')} />
      </div>

      <div className="flex flex-col gap-2">
        {label('email', contact.labels.email, true)}
        <input type="email" autoComplete="email" inputMode="email" {...fieldProps('email')} />
        {error('email')}
      </div>

      <div className="flex flex-col gap-2">
        {label('phone', contact.labels.phone)}
        <input type="tel" autoComplete="tel" inputMode="tel" {...fieldProps('phone')} />
      </div>

      <div className="col-span-full flex flex-col gap-2">
        {label('topic', contact.labels.topic, true)}
        <select
          value={topic}
          {...fieldProps('topic')}
          onChange={(event) => {
            setTopic(event.target.value);
            dismiss('topic');
          }}
        >
          <option value="">{contact.topicPlaceholder}</option>
          {contact.topics.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {error('topic')}
      </div>

      <div className="col-span-full flex flex-col gap-2">
        {label('message', contact.labels.message, true)}
        <textarea rows={5} {...fieldProps('message', 'min-h-35 resize-y')} />
        {error('message')}
      </div>

      <div className="col-span-full flex flex-col gap-2">
        {label('link', contact.labels.link)}
        <input type="url" inputMode="url" placeholder="https://" {...fieldProps('link')} />
        {error('link')}
      </div>

      <div className="col-span-full flex flex-col gap-2">
        <label className="grid min-h-11 cursor-pointer grid-cols-[22px_minmax(0,1fr)] items-start gap-3 text-[15px] leading-snug">
          <input
            type="checkbox"
            name="consent"
            aria-invalid={errorFor('consent') ? true : undefined}
            onChange={() => dismiss('consent')}
            className="size-5.5 cursor-pointer accent-accent"
          />
          <span>
            {contact.labels.consent}{' '}
            <Link
              href={`#${sectionIds.privacy}`}
              className="underline underline-offset-[3px] hover:text-accent"
            >
              {dictionary.footer.privacy}
            </Link>
            <span aria-hidden className="text-accent">
              {' '}
              *
            </span>
          </span>
        </label>
        {error('consent')}
      </div>

      {state.status === 'failed' && (
        <div
          role="alert"
          className="col-span-full flex animate-fade-up flex-col gap-1 rounded-xl border border-danger p-4"
        >
          <strong className="text-[15px] text-danger">{contact.failure.title}</strong>
          <span className="text-sm text-muted">{contact.failure.description}</span>
        </div>
      )}

      <div className="col-span-full mt-2 flex flex-wrap items-center justify-between gap-4">
        <Button type="submit" disabled={pending} aria-busy={pending}>
          {pending
            ? contact.sending
            : topic === contact.topics[0]
              ? contact.submitProject
              : contact.submitMessage}
          <ArrowRight />
        </Button>
        <span className="text-[13px] text-muted">{contact.required}</span>
      </div>
    </form>
  );
}

function SuccessPanel({ dictionary, onReset }: { dictionary: Dictionary; onReset: () => void }) {
  return (
    <div
      role="status"
      className="flex min-h-90 animate-fade-up flex-col justify-center gap-5.5 rounded-3xl border border-line bg-surface p-[clamp(28px,4vw,56px)]"
    >
      <LogoMark width={64} />
      <h3 className="text-[clamp(28px,3vw,42px)] leading-tight font-light tracking-[-0.03em]">
        {dictionary.contact.success.title}
      </h3>
      <p className="max-w-[44ch] text-[17px] text-muted">
        {dictionary.contact.success.description}
      </p>
      <Button
        type="button"
        variant="outline"
        size="md"
        onClick={onReset}
        className="mt-2 self-start"
      >
        {dictionary.contact.success.again}
      </Button>
    </div>
  );
}
